// ============================================
// Age Verification Approvals — duyệt xác minh 18+
// ============================================
// Khách không mua được hàng 18+ (rượu/thuốc lá) cho tới khi nhân viên xem
// giấy tờ và bấm duyệt ở màn này. Quyền: STORE_ADMIN | AI_ADMIN.
//
// - Danh sách hồ sơ PENDING (GET /customers/age-verifications/ — BE đang bổ
//   sung; contract xem customers.service.ts).
// - Chi tiết trong modal: ảnh giấy tờ (CMND/CCCD 2 ảnh — mặt trước/mặt sau),
//   phương thức, thời điểm nộp.
// - Duyệt (bắt buộc nhập tuổi đọc từ giấy tờ) / Từ chối (bắt buộc lý do —
//   lý do hiển thị lại cho khách trên app).

import { useEffect, useState } from 'react';
import type { AgeVerificationQueueItem, UUID } from '@conai/shared-types';
import { useI18n } from '../../i18n';
import { PageContainer, PageHeader, Pagination } from '../../components/shared';
import { useDebounce } from '../../hooks/useDebounce';
import { useLockBodyScroll } from '../../hooks/useLockBodyScroll';
import {
  useAgeVerifications,
  useAgeVerificationDetail,
  useReviewAgeVerification,
} from '../../hooks/useAgeVerifications';

const PAGE_SIZE = 10;
const MIN_AGE = 18;

/**
 * Ba trạng thái xem được.
 *
 * `PENDING` là hàng đợi việc phải làm nên để đầu. Hai tab kia không phải để
 * "xem lịch sử" cho vui: bản ghi VERIFIED cần duyệt lại được (vd duyệt nhầm
 * tuổi), và không có tab này thì không có đường nào tới nó — phần xử lý duyệt
 * lại sẽ nằm chết trong code.
 */
const STATUS_TABS = ['PENDING', 'VERIFIED', 'REJECTED'] as const;
type StatusTab = (typeof STATUS_TABS)[number];

const STATUS_TAB_LABEL: Record<StatusTab, string> = {
  PENDING: 'avTabPending',
  VERIFIED: 'avTabVerified',
  REJECTED: 'avTabRejected',
};

const STATUS_BADGE_CLASS: Record<string, string> = {
  PENDING: 'bg-amber-50 text-amber-700',
  VERIFIED: 'bg-emerald-50 text-emerald-700',
  REJECTED: 'bg-red-50 text-red-700',
};

const METHOD_LABEL: Record<string, string> = {
  DIIA: 'Diia',
  PASSPORT: 'Passport',
  ID_CARD: 'ID card',
};

function formatDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB');
}

function ageFromDob(value?: string | null): number | null {
  if (!value) return null;
  const dob = new Date(value);
  if (Number.isNaN(dob.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const beforeBirthday =
    now.getMonth() < dob.getMonth() ||
    (now.getMonth() === dob.getMonth() && now.getDate() < dob.getDate());
  if (beforeBirthday) age -= 1;
  return age;
}

function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export function AgeVerificationsPage() {
  const { t } = useI18n();
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 500);
  const [statusTab, setStatusTab] = useState<StatusTab>('PENDING');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeItem, setActiveItem] = useState<AgeVerificationQueueItem | null>(null);
  // Ảnh đang phóng to trong modal (null = không)
  const [zoomUrl, setZoomUrl] = useState<string | null>(null);

  // Form duyệt / từ chối
  const [verifiedAge, setVerifiedAge] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  useLockBodyScroll(activeItem !== null);

  const listQuery = useAgeVerifications({
    status: statusTab,
    search: debouncedSearch || undefined,
    page: currentPage,
    page_size: PAGE_SIZE,
  });
  const detailQuery = useAgeVerificationDetail(activeItem?.customer_id ?? null);
  const review = useReviewAgeVerification();

  const rows = listQuery.data?.results ?? [];
  const totalItems = listQuery.data?.count ?? rows.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  const detail = detailQuery.data;

  /**
   * Ảnh giấy tờ, CHỐT LẠI ngay lần đầu tải được.
   *
   * Backend xoá ảnh ngay sau khi có quyết định duyệt/từ chối (RODO, 17/08/2026),
   * nên mọi response sau đó đều trả mảng rỗng. Nếu render thẳng từ query thì
   * khoảnh khắc admin bấm Duyệt, ảnh trên màn hình biến mất trước khi modal kịp
   * đóng — trông như vừa làm hỏng dữ liệu. Giữ bản chụp trong state để màn hình
   * chỉ đổi khi admin mở hồ sơ khác.
   */
  const [frozenDocumentUrls, setFrozenDocumentUrls] = useState<string[]>([]);

  useEffect(() => {
    const urls = (
      detail?.document_urls ??
      (detail?.document_url ? [detail.document_url] : null) ??
      activeItem?.document_urls ??
      []
    ).filter(Boolean);
    // Chỉ ghi khi CÓ ảnh. Mảng rỗng là tín hiệu "đã xoá sau khi duyệt", không
    // phải dữ liệu mới — ghi đè bằng nó là đúng cái lỗi cần tránh.
    if (urls.length > 0) setFrozenDocumentUrls(urls);
  }, [detail, activeItem]);

  const documentUrls = frozenDocumentUrls;
  const method = detail?.method ?? activeItem?.method ?? null;
  const submittedAt = detail?.submitted_at ?? activeItem?.submitted_at ?? null;
  // Ngày sinh KHÁCH TỰ KHAI khi nộp — admin đối chiếu với giấy tờ
  const declaredDob = detail?.date_of_birth ?? activeItem?.date_of_birth ?? null;
  const declaredAge = ageFromDob(declaredDob);

  /**
   * Hồ sơ này đã có quyết định trước đó (duyệt lại / xem lại).
   *
   * Bản ghi VERIFIED KHÔNG còn `date_of_birth` — đã bị xoá cùng ảnh ở lần duyệt
   * trước. `verified_age` mới là dữ kiện. Đây là trạng thái BÌNH THƯỜNG, không
   * được hiển thị thành "khách chưa khai ngày sinh". Bản ghi REJECTED thì vẫn
   * giữ `date_of_birth` để duyệt lại được.
   */
  const decidedStatus = detail?.status ?? activeItem?.status ?? null;
  const isAlreadyVerified = decidedStatus === 'VERIFIED';
  // Duyệt lại một bản ghi VERIFIED mà không truyền `verified_age` -> BE trả 400.
  // Không prefill được vì không còn ngày sinh, nên phải nói rõ để admin nhập tay
  // thay vì nhìn ô trống và tưởng màn hình lỗi.
  const needsManualAge = isAlreadyVerified && declaredAge == null;

  // Prefill tuổi từ ngày sinh khách khai — admin chỉ cần đối chiếu giấy tờ
  // rồi bấm Duyệt; lệch với giấy tờ thì sửa tay được.
  useEffect(() => {
    if (declaredAge != null && activeItem) {
      setVerifiedAge(String(declaredAge));
    }
  }, [declaredAge, activeItem]);

  const openReview = (item: AgeVerificationQueueItem) => {
    setActiveItem(item);
    setVerifiedAge('');
    setRejectReason('');
    setFormError(null);
    setFrozenDocumentUrls(item.document_urls?.filter(Boolean) ?? []);
  };

  const closeReview = () => {
    if (review.isPending) return;
    setActiveItem(null);
    setZoomUrl(null);
  };

  const handleApprove = () => {
    if (!activeItem || review.isPending) return;
    const age = Number.parseInt(verifiedAge, 10);
    if (!Number.isFinite(age) || age < MIN_AGE || age > 120) {
      setFormError(t('avVerifiedAgeInvalid'));
      return;
    }
    setFormError(null);
    review.mutate(
      {
        customerId: activeItem.customer_id as UUID,
        data: { status: 'VERIFIED', verified_age: age },
      },
      { onSuccess: closeReview }
    );
  };

  const handleReject = () => {
    if (!activeItem || review.isPending) return;
    const reason = rejectReason.trim();
    if (reason.length < 5) {
      setFormError(t('avRejectReasonRequired'));
      return;
    }
    setFormError(null);
    review.mutate(
      {
        customerId: activeItem.customer_id as UUID,
        data: { status: 'REJECTED', rejection_reason: reason },
      },
      { onSuccess: closeReview }
    );
  };

  return (
    <PageContainer>
      <PageHeader title={t('ageVerifications')} subtitle={t('ageVerificationsSubtitle')} />

      {/* Tabs trạng thái + tìm kiếm.
          Bug #194: `flex flex-wrap gap-4` không có breakpoint nên trên màn hình
          hẹp cụm 3 tab và ô tìm kiếm (`flex-1`) chen nhau trên cùng một hàng
          trước khi kịp xuống dòng, ô nhập bị bóp còn vài chục pixel. Xếp dọc
          mặc định, chỉ ghép hàng từ `sm:` trở lên — bám theo NodeDirectoryTable. */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
        <div className="flex overflow-x-auto rounded-lg border border-slate-200 bg-white p-1">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setStatusTab(tab);
                setCurrentPage(1);
              }}
              className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium ${
                statusTab === tab
                  ? 'bg-[#0288D1] text-white'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              {t(STATUS_TAB_LABEL[tab])}
            </button>
          ))}
        </div>

        {/* Bug #195: ô này viết tay, không dùng SearchInput chung nên thiếu hai
            thứ mà mọi ô tìm kiếm khác trong app đã có: `enterKeyHint="search"`
            (bàn phím ảo hiện nút "Tìm" thay vì nút xuống dòng) và blur khi nhấn
            Enter/OK. Không có chúng thì bấm OK không có handler nào phản hồi,
            bàn phím đứng nguyên che mất kết quả vừa lọc.

            Không nằm trong <form> nên cũng không có submit ngầm để dựa vào —
            phải tự gọi blur. */}
        <input
          type="search"
          enterKeyHint="search"
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            setCurrentPage(1);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
          placeholder={t('avSearchPlaceholder')}
          className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-primary-500 sm:max-w-md sm:flex-1"
        />
      </div>

      {/* Hồ sơ đã có quyết định thì ảnh giấy tờ đã bị xoá theo RODO — nói trước
          ở đây để admin không mở từng hồ sơ ra tìm ảnh rồi tưởng hỏng dữ liệu. */}
      {statusTab === 'VERIFIED' ? (
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
          {t('avVerifiedTabHint')}
        </div>
      ) : null}

      {/* Table */}
      <div className="overflow-hidden rounded-xl bg-white" style={{ boxShadow: '0px 1px 2px rgba(0,0,0,0.05)' }}>
        {listQuery.isLoading ? (
          <div className="px-6 py-12 text-center text-sm text-slate-500">{t('loading')}</div>
        ) : listQuery.isError ? (
          <div className="px-6 py-12 text-center">
            <p className="mb-3 text-sm text-slate-500">{t('avLoadError')}</p>
            <button
              onClick={() => listQuery.refetch()}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              {t('avRetry')}
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-slate-500">{t('avEmpty')}</div>
        ) : (
          /* Bug #194: bảng 6 cột với `px-6` mỗi ô nằm trong khung `overflow-hidden`
             — trên mobile không cuộn ngang được nên nội dung bị nén và cắt cụt.
             Bọc `overflow-x-auto` + `whitespace-nowrap` và giảm padding xuống
             `px-4`, đúng cách RestrictedSalesPage (cùng kiểu bảng) đang làm. */
          <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-xs uppercase text-slate-500">
                <th className="whitespace-nowrap px-4 py-3 font-medium">{t('avCustomer')}</th>
                <th className="whitespace-nowrap px-4 py-3 font-medium">{t('avContact')}</th>
                <th className="whitespace-nowrap px-4 py-3 font-medium">{t('avMethod')}</th>
                <th className="whitespace-nowrap px-4 py-3 font-medium">{t('avSubmittedAt')}</th>
                <th className="whitespace-nowrap px-4 py-3 font-medium">{t('avStatus')}</th>
                <th className="whitespace-nowrap px-4 py-3 text-right font-medium">{t('action')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((item) => (
                <tr key={item.customer_id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60">
                  <td className="whitespace-nowrap px-4 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-50 text-sm font-bold text-[#0288D1]">
                        {(item.customer_name || '?').charAt(0).toUpperCase()}
                      </div>
                      <span className="font-medium text-slate-900">{item.customer_name || '—'}</span>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3.5 text-slate-600">
                    <div>{item.customer_email || '—'}</div>
                    {item.customer_phone ? (
                      <div className="text-xs text-slate-400">{item.customer_phone}</div>
                    ) : null}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3.5">
                    <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
                      {METHOD_LABEL[item.method ?? ''] ?? item.method ?? '—'}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3.5 text-slate-600">{formatDateTime(item.submitted_at)}</td>
                  <td className="whitespace-nowrap px-4 py-3.5">
                    <span
                      className={`rounded-md px-2 py-1 text-xs font-medium ${
                        STATUS_BADGE_CLASS[item.status] ?? 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      {t(STATUS_TAB_LABEL[item.status as StatusTab] ?? 'avStatus')}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3.5 text-right">
                    <button
                      onClick={() => openReview(item)}
                      className="rounded-lg bg-[#0288D1] px-4 py-2 text-xs font-semibold text-white hover:bg-[#0277BD]"
                    >
                      {/* Hồ sơ đã có quyết định thì đây là "duyệt lại", không
                          phải "duyệt" — nói đúng việc để admin không tưởng mình
                          đang xử lý một hồ sơ chờ. */}
                      {item.status === 'PENDING' ? t('avReview') : t('avReReview')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}

        {rows.length > 0 ? (
          <div className="border-t border-slate-100 px-6 py-3">
            <Pagination
              variant="numbered"
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={PAGE_SIZE}
              onPageChange={setCurrentPage}
            />
          </div>
        ) : null}
      </div>

      {/* Review modal */}
      {activeItem ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6">
            <div className="mb-4 flex items-start justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-900">{t('avReviewTitle')}</h2>
                <p className="text-sm text-slate-500">{activeItem.customer_name}</p>
              </div>
              <button onClick={closeReview} className="text-slate-400 hover:text-slate-600">
                <XIcon />
              </button>
            </div>

            {/* Meta. Bug #194: `grid-cols-2` cứng làm hai cột dồn lại còn ~150px
                trên điện thoại, ngày giờ và email đều xuống dòng lởm chởm. Một
                cột trên mobile, hai cột từ `sm:`. */}
            <div className="mb-4 grid grid-cols-1 gap-3 rounded-xl bg-slate-50 p-4 text-sm sm:grid-cols-2">
              <div>
                <p className="text-xs text-slate-500">{t('avMethod')}</p>
                <p className="font-medium text-slate-900">
                  {method ? METHOD_LABEL[method] ?? method : '—'}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">{t('avSubmittedAt')}</p>
                <p className="font-medium text-slate-900">{formatDateTime(submittedAt)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">{t('avDeclaredDob')}</p>
                <p className="font-medium text-slate-900">
                  {declaredDob
                    ? `${formatDate(declaredDob)}${declaredAge != null ? ` (${declaredAge})` : ''}`
                    : '—'}
                </p>
                {/* Hồ sơ đã duyệt thì ngày sinh đã bị xoá theo RODO — nói rõ là
                    "đã xoá sau khi duyệt", đừng để admin đọc dấu gạch ngang
                    thành "khách chưa khai ngày sinh". */}
                {needsManualAge ? (
                  <p className="mt-0.5 text-xs text-slate-400">{t('avDobErasedHint')}</p>
                ) : null}
              </div>
              <div>
                <p className="text-xs text-slate-500">{t('avContact')}</p>
                <p className="font-medium text-slate-900">
                  {[activeItem.customer_email, activeItem.customer_phone].filter(Boolean).join(' · ') || '—'}
                </p>
              </div>
            </div>

            {/* Documents */}
            <p className="mb-2 text-sm font-semibold text-slate-900">{t('avDocuments')}</p>
            {detailQuery.isLoading ? (
              <p className="mb-4 text-sm text-slate-500">{t('loading')}</p>
            ) : documentUrls.length === 0 ? (
              // Hai lý do rất khác nhau cùng cho ra "không có ảnh": hồ sơ dùng
              // định danh số (không bao giờ có ảnh), và hồ sơ đã duyệt xong nên
              // ảnh đã bị xoá. Nói "không có ảnh giấy tờ" cho ca thứ hai là để
              // admin đi tìm một thứ đã bị xoá đúng quy trình.
              <p className="mb-4 text-sm text-slate-500">
                {isAlreadyVerified ? t('avDocumentsErased') : t('avNoDocuments')}
              </p>
            ) : (
              <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {documentUrls.map((url, index) => (
                  <button
                    key={url}
                    onClick={() => setZoomUrl(url)}
                    className="group relative overflow-hidden rounded-xl border border-slate-200"
                  >
                    <img src={url} alt={`document-${index + 1}`} className="h-36 w-full object-cover" />
                    <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
                      {documentUrls.length > 1
                        ? index === 0
                          ? t('avFrontSide')
                          : t('avBackSide')
                        : t('avDocuments')}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* Approve */}
            <div className="mb-3 rounded-xl border border-slate-200 p-4">
              <label className="mb-1 block text-sm font-semibold text-slate-900">
                {t('avVerifiedAge')}
              </label>
              {declaredAge != null ? (
                <p className="mb-2 text-xs text-slate-500">{t('avPrefilledHint')}</p>
              ) : needsManualAge ? (
                // Ô tuổi trống ở đây là hệ quả của việc xoá dữ liệu, không phải
                // lỗi tải. Không nói ra thì admin sẽ bấm Duyệt với ô rỗng và ăn
                // 400 mà không hiểu vì sao.
                <p className="mb-2 text-xs text-amber-600">{t('avManualAgeHint')}</p>
              ) : null}
              {/* Bug #194: hàng này trước đây là `flex` với ô nhập rộng cố định
                  28 (112px) — trên mobile nút Duyệt bị đẩy tràn khỏi khung. */}
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  type="number"
                  min={MIN_AGE}
                  max={120}
                  value={verifiedAge}
                  onChange={(e) => setVerifiedAge(e.target.value)}
                  placeholder={t('avVerifiedAgePlaceholder')}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary-500 sm:w-28"
                />
                <button
                  onClick={handleApprove}
                  disabled={review.isPending}
                  className="flex-1 rounded-lg bg-[#22C55E] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16A34A] disabled:opacity-50"
                >
                  {t('avApprove')}
                </button>
              </div>
            </div>

            {/* Reject */}
            <div className="rounded-xl border border-slate-200 p-4">
              <label className="mb-1 block text-sm font-semibold text-slate-900">
                {t('avRejectReason')}
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder={t('avRejectReasonPlaceholder')}
                rows={2}
                maxLength={500}
                className="mb-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary-500"
              />
              <button
                onClick={handleReject}
                disabled={review.isPending}
                className="w-full rounded-lg border border-[#EF4444] px-4 py-2 text-sm font-semibold text-[#EF4444] hover:bg-red-50 disabled:opacity-50"
              >
                {t('avReject')}
              </button>
            </div>

            {formError ? (
              <p className="mt-3 text-sm text-[#EF4444]">{formError}</p>
            ) : null}
          </div>

          {/* Zoom ảnh giấy tờ */}
          {zoomUrl ? (
            <div
              className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-6"
              onClick={() => setZoomUrl(null)}
            >
              <img src={zoomUrl} alt="document" className="max-h-full max-w-full object-contain" />
            </div>
          ) : null}
        </div>
      ) : null}
    </PageContainer>
  );
}
